/* eslint-disable camelcase */
import mixpanel, {type Mixpanel} from 'mixpanel-browser';
import Config from 'hooks/config';

/** @module utils/mixpanel-flags */

/**
 * Name of the dedicated Mixpanel instance. Analytics reach Mixpanel through Segment,
 * so this instance is used for feature flags only and never tracks events.
 */
const INSTANCE_NAME = 'controlPanelFlags';

/** Runtime properties Mixpanel flag targeting rules are evaluated against. */
export type FlagCustomProperties = Record<string, string | null>;

let instance: Mixpanel | null = null;

/**
 * Waits for a flags request to settle. A failed request (e.g. blocked by an ad blocker) is logged,
 * and every flag falls back to disabled.
 */
const waitForFlags = async (request: Promise<unknown>): Promise<void> => {
    try {
        await request;
    } catch (error) {
        console.error('Failed to load feature flags:', error);

        return;
    }

    if (!instance?.flags.are_flags_ready()) {
        console.error('Failed to load feature flags');
    }
};

/**
 * Builds the flag evaluation context. A provided `distinctId` replaces the SDK's anonymous device id.
 */
const buildContext = (
    customProperties: FlagCustomProperties,
    distinctId?: string | null
) => {
    const context = {
        custom_properties: customProperties,
        ...(distinctId && {distinct_id: distinctId}),
    };

    return context;
};

/**
 * Initializes the flags-only Mixpanel instance and fetches flags for the given context.
 * Subsequent calls replace the context and refresh the flags.
 *
 * @param customProperties - Properties used by the flag targeting rules.
 * @param distinctId - The SpotHero user id used as the flag assignment key.
 * @returns A promise that resolves once the flags request settles.
 */
const init = (
    customProperties: FlagCustomProperties,
    distinctId?: string | null
): Promise<void> => {
    const context = buildContext(customProperties, distinctId);

    if (instance) {
        return waitForFlags(
            instance.flags.update_context(context, {replace: true})
        );
    }

    if (!Config.mixpanelToken) {
        return Promise.resolve();
    }

    instance = mixpanel.init(
        Config.mixpanelToken,
        {
            api_host: Config.mixpanelApiHost,
            flags: {context},
            persistence_name: 'cp_feature_flags',
            track_pageview: false,
            autocapture: false,
        },
        INSTANCE_NAME
    );

    return waitForFlags(instance.flags.get_all_variants());
};

/**
 * Resolves once the current flags request settles. Resolves immediately if flags were never initialized.
 */
const whenReady = (): Promise<void> => {
    if (!instance) {
        return Promise.resolve();
    }

    return waitForFlags(instance.flags.get_all_variants());
};

/**
 * Gets the value served for a flag without reporting an exposure event.
 *
 * @param key - The flag key.
 * @returns The served value, or `null` if flags aren't loaded or the flag isn't served.
 */
const getFlagValue = <T = unknown>(key: string): T | null => {
    if (!instance) {
        return null;
    }

    const variant = instance.flags.get_all_variants_sync().get(key);

    return (variant?.value as T) ?? null;
};

const MixpanelFlags = {
    init,
    whenReady,
    getFlagValue,
};

export default MixpanelFlags;
